import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { buildSystemPromptFromTemplate, CHATBOT_PROMPT_TEMPLATES } from '@workspace/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { MESA_TOOL_OPTIONS, mesaToolsToPersist } from '../api/mesaTools';
import { mesaApi } from '../api/mesaApi';
import { contactAgentError } from '../../contact-agent/api/contactAgentScope';
import { ContactAgentPage, ContactAgentPageHeader } from '../../contact-agent/components/ContactAgentPageHeader';
import { ContactAgentTenantPicker } from '../../contact-agent/components/ContactAgentTenantPicker';
import { EscalationRosterFields } from '../../contact-agent/components/EscalationRosterFields';
import { useContactAgentTenant } from '../../contact-agent/hooks/useContactAgentTenant';

import type { ContactAgentAvailableBot } from '@workspace/types';

type BotOption = ContactAgentAvailableBot;

export function MesaAgentPage() {
  const navigate = useNavigate();
  const { agentId } = useParams<{ agentId: string }>();
  const isNew = !agentId || agentId === 'new';
  const { tenantId, tenants, isSuperAdmin, ready, selectTenant } = useContactAgentTenant();
  const [bots, setBots] = useState<BotOption[]>([]);
  const [botId, setBotId] = useState('');
  const [botName, setBotName] = useState('');
  const [name, setName] = useState('Mesa de ayuda');
  const [objective, setObjective] = useState(
    'Atender consultas de primer nivel con datos reales del ticket y derivar a un operador cuando haga falta.',
  );
  const [systemPrompt, setSystemPrompt] = useState('');
  const [templateId, setTemplateId] = useState('helpdesk');
  const [tools, setTools] = useState<string[]>(MESA_TOOL_OPTIONS.map(item => item.name));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!ready) {
      return;
    }
    if (!isNew && agentId) {
      void mesaApi
        .getAgent(agentId)
        .then(agent => {
          setBotId(agent.botId);
          setBotName(agent.bot?.name ?? agent.name);
          setName(agent.name);
          setObjective(agent.objective ?? '');
          setTools(
            agent.toolsEnabled.length > 0 ? agent.toolsEnabled : MESA_TOOL_OPTIONS.map(item => item.name),
          );
          setSystemPrompt(agent.bot?.systemPrompt ?? '');
        })
        .catch(err => setError(contactAgentError(err)));
      return;
    }

    setBotId('');
    setBotName('');
    setSystemPrompt(buildSystemPromptFromTemplate('helpdesk'));
    setTemplateId('helpdesk');
    setError('');
    void mesaApi
      .listAvailableBots()
      .then(options => {
        setBots(options);
        const firstEligible = options.find(bot => bot.identificationEligible);
        if (firstEligible) {
          setBotId(firstEligible.botId);
        } else if (options[0]) {
          setBotId(options[0].botId);
        }
      })
      .catch(err => setError(contactAgentError(err)));
  }, [ready, tenantId, isNew, agentId]);

  const applyPromptTemplate = (nextTemplateId: string) => {
    setTemplateId(nextTemplateId);
    setSystemPrompt(buildSystemPromptFromTemplate(nextTemplateId));
  };

  const toggleTool = (toolName: string) => {
    setTools(current =>
      current.includes(toolName) ? current.filter(item => item !== toolName) : [...current, toolName],
    );
  };

  const selectedBot = bots.find(bot => bot.botId === botId);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!botId || !name.trim()) {
      setError('Elegí un bot y un nombre.');
      return;
    }
    if (isNew && selectedBot && !selectedBot.identificationEligible) {
      setError(
        selectedBot.identificationIssues[0] ??
          'El bot debe tener al menos un método de identificación unívoca (whitelist o verificación web).',
      );
      return;
    }
    if (!systemPrompt.trim()) {
      setError('El prompt del bot no puede quedar vacío.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim().slice(0, 255),
        objective: objective.trim().slice(0, 4000),
        toolsEnabled: mesaToolsToPersist(tools),
        systemPrompt: systemPrompt.trim().slice(0, 32000),
      };
      if (isNew) {
        await mesaApi.createAgent({ botId, ...payload });
      } else if (agentId) {
        await mesaApi.updateAgent(agentId, payload);
      }
      navigate('/admin/mesa');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar el agente');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ContactAgentPage>
      <ContactAgentPageHeader
        title={isNew ? 'Nuevo agente de mesa' : 'Editar agente de mesa'}
        description='Solo bots con identificación unívoca. El agente atiende por canal directo; el conserje no deriva a mesa de ayuda.'
        toolbar={
          isSuperAdmin ? (
            <ContactAgentTenantPicker tenants={tenants} tenantId={tenantId} onChange={selectTenant} />
          ) : null
        }
      />

      <Card className='rounded-2xl shadow-sm'>
        <CardHeader>
          <CardTitle>Datos del agente</CardTitle>
        </CardHeader>
        <CardContent>
          <form className='space-y-6' onSubmit={handleSubmit}>
            <div className='space-y-2'>
              <Label htmlFor='botId'>Bot</Label>
              {isNew ? (
                <select
                  id='botId'
                  className='flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm'
                  value={botId}
                  onChange={event => setBotId(event.target.value)}
                >
                  {bots.length === 0 ? <option value=''>No hay bots disponibles</option> : null}
                  {bots.map(bot => (
                    <option key={bot.botId} value={bot.botId} disabled={!bot.identificationEligible}>
                      {bot.name}
                      {!bot.identificationEligible ? ' (sin identificación)' : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <Input id='botId' value={botName || botId} disabled />
              )}
              {isNew && selectedBot && !selectedBot.identificationEligible ? (
                <div className='rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm'>
                  <p className='font-medium text-destructive'>Este bot no puede usarse como agente de mesa</p>
                  <p className='mt-1 text-muted-foreground'>
                    {selectedBot.identificationIssues.join(' ') ||
                      'Configurá whitelist en WhatsApp/Telegram o acceso web verificado en la instancia del bot.'}
                  </p>
                </div>
              ) : null}
              {isNew && selectedBot?.identificationEligible ? (
                <p className='text-xs text-muted-foreground'>
                  Identificación habilitada: {selectedBot.identificationMethods.join(', ')}.
                </p>
              ) : null}
              {botId ? (
                <p className='text-xs text-muted-foreground'>
                  También podés editar modelo y API key en{' '}
                  <Link className='underline' to={`/admin/chatbot/bots/${botId}`}>
                    Chatbot → Bots
                  </Link>
                  .
                </p>
              ) : null}
            </div>
            <div className='space-y-2'>
              <Label htmlFor='name'>Nombre</Label>
              <Input id='name' value={name} maxLength={255} onChange={event => setName(event.target.value)} />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='promptTemplate'>Plantilla de prompt</Label>
              <div className='flex flex-wrap items-end gap-2'>
                <select
                  id='promptTemplate'
                  className='flex h-10 min-w-[16rem] flex-1 rounded-md border border-input bg-background px-3 text-sm'
                  value={templateId}
                  onChange={event => applyPromptTemplate(event.target.value)}
                >
                  {CHATBOT_PROMPT_TEMPLATES.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name} — {template.description}
                    </option>
                  ))}
                </select>
                <Button type='button' variant='outline' onClick={() => applyPromptTemplate(templateId)}>
                  Aplicar plantilla
                </Button>
              </div>
            </div>
            <div className='space-y-2'>
              <Label htmlFor='systemPrompt'>Prompt del bot</Label>
              <Textarea
                id='systemPrompt'
                rows={10}
                maxLength={32000}
                value={systemPrompt}
                onChange={event => setSystemPrompt(event.target.value)}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='objective'>Objetivo de la mesa</Label>
              <Textarea
                id='objective'
                rows={3}
                maxLength={4000}
                value={objective}
                onChange={event => setObjective(event.target.value)}
              />
            </div>
            <div className='space-y-3'>
              <Label>Herramientas</Label>
              <div className='grid gap-3 sm:grid-cols-2'>
                {MESA_TOOL_OPTIONS.map(tool => (
                  <label
                    key={tool.name}
                    className='flex cursor-pointer gap-3 rounded-lg border p-3 hover:bg-muted/40'
                  >
                    <input
                      type='checkbox'
                      className='mt-1'
                      checked={tools.includes(tool.name)}
                      onChange={() => toggleTool(tool.name)}
                    />
                    <span>
                      <span className='block text-sm font-medium'>{tool.label}</span>
                      <span className='block text-xs text-muted-foreground'>{tool.description}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
            {error ? <p className='text-sm text-destructive'>{error}</p> : null}
            <div className='flex flex-wrap gap-2'>
              <Button
                type='submit'
                disabled={saving || !botId || (isNew && selectedBot ? !selectedBot.identificationEligible : false)}
              >
                {saving ? 'Guardando…' : 'Guardar agente'}
              </Button>
              <Button type='button' variant='outline' asChild>
                <Link to='/admin/mesa'>Cancelar</Link>
              </Button>
            </div>
          </form>
          {!isNew && agentId ? (
            <div className='mt-8'>
              <EscalationRosterFields agentId={agentId} apiBase='/admin/mesa' />
            </div>
          ) : null}
        </CardContent>
      </Card>
    </ContactAgentPage>
  );
}
